import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Select from "react-select";
import { filterAndSlice } from "../util/selectUtils";
import MultiSelectField from "./MultiSelectField";

const STATIC_FIELD_IDS = [
  "manual_data_exclusion_date_from",
  "manual_data_exclusion_date_to",
  "manual_data_exclusion_ticket",
  "manual_data_exclusion_reason",
];

const TICKET_MAX_LEN = 255;
const REASON_MAX_LEN = 5000;

const ExclusionFormFields = ({ clients }) => {
  const [step, setStep] = useState(1);
  const [clientInputValue, setClientInputValue] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retailers, setRetailers] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [measureTypes, setMeasureTypes] = useState([]);
  const [journeys, setJourneys] = useState([]);

  const [retailerValues, setRetailerValues] = useState([]);
  const [measureValues, setMeasureValues] = useState([]);
  const [measureTypeValues, setMeasureTypeValues] = useState([]);
  const [journeyValues, setJourneyValues] = useState([]);
  const [staticFieldsFilled, setStaticFieldsFilled] = useState(false);

  const abortControllerRef = useRef(null);

  const clientOptions = useMemo(
    () => filterAndSlice(clients, clientInputValue),
    [clients, clientInputValue]
  );

  const isClientAll = selectedClient && selectedClient.value === "all";
  const hasMeasures = measureValues.length > 0;
  const hasMeasureTypes = measureTypeValues.length > 0;
  const hasJourneys = journeyValues.length > 0;
  const retailerHasAll = retailerValues.includes("all");
  const allClientAllRetailerWarning = isClientAll && retailerHasAll;
  const showDetails = step === 2 && !loading && !error;

  const onClientChange = useCallback((opt) => {
    setSelectedClient(opt);
    const el = document.getElementById("manual_data_exclusion_client_slug");
    if (el) el.value = opt ? opt.value : "";
  }, []);

  const updateSubmitButton = useCallback((enabled) => {
    const btn = document.getElementById("submit-exclusion-btn");
    if (!btn) return;
    btn.disabled = !enabled;
    if (enabled) {
      btn.classList.remove("bg-indigo-300", "cursor-not-allowed");
      btn.classList.add("bg-indigo-600", "hover:bg-indigo-700", "cursor-pointer");
    } else {
      btn.classList.remove("bg-indigo-600", "hover:bg-indigo-700", "cursor-pointer");
      btn.classList.add("bg-indigo-300", "cursor-not-allowed");
    }
  }, []);

  const clearHiddenFields = () => {
    ["retailers", "measures", "measure_types", "journeys"].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.value = "";
    });
  };

  const onContinue = useCallback(() => {
    if (!selectedClient) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setStep(2);
    setLoading(true);
    setError(null);

    fetch(
      `/manual_data_exclusions/form_data?client_slug=${encodeURIComponent(selectedClient.value)}`,
      { signal: controller.signal }
    )
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load form data (${res.status})`);
        return res.json();
      })
      .then((data) => {
        setRetailers(data.retailers || []);
        setMeasures(data.measures || []);
        setMeasureTypes(data.measure_types || []);
        setJourneys(data.journeys || []);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setError(err.message);
        setLoading(false);
      });
  }, [selectedClient]);

  const onChangeClient = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setStep(1);
    setSelectedClient(null);
    setRetailers([]);
    setMeasures([]);
    setMeasureTypes([]);
    setJourneys([]);
    setRetailerValues([]);
    setMeasureValues([]);
    setMeasureTypeValues([]);
    setJourneyValues([]);
    setStaticFieldsFilled(false);
    setError(null);

    const el = document.getElementById("manual_data_exclusion_client_slug");
    if (el) el.value = "";

    clearHiddenFields();
    updateSubmitButton(false);
  }, [updateSubmitButton]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const details = document.getElementById("step-form-details");
    const submit = document.getElementById("submit-section");
    if (details) details.style.display = showDetails ? "block" : "none";
    if (submit) submit.style.display = showDetails ? "block" : "none";
  }, [showDetails]);

  useEffect(() => {
    if (!showDetails) return;

    const checkStaticFields = () => {
      const allFilled = STATIC_FIELD_IDS.every((id) => {
        const el = document.getElementById(id);
        if (!el || el.value.trim().length === 0) return false;
        if (id === "manual_data_exclusion_ticket" && el.value.length > TICKET_MAX_LEN) return false;
        if (id === "manual_data_exclusion_reason" && el.value.length > REASON_MAX_LEN) return false;
        return true;
      });
      setStaticFieldsFilled(allFilled);
    };

    const els = STATIC_FIELD_IDS.map((id) => document.getElementById(id)).filter(Boolean);
    els.forEach((el) => el.addEventListener("input", checkStaticFields));
    checkStaticFields();

    return () => {
      els.forEach((el) => el.removeEventListener("input", checkStaticFields));
    };
  }, [showDetails]);

  useEffect(() => {
    if (step !== 2 || loading || error) {
      updateSubmitButton(false);
      return;
    }

    const allFilled = !!selectedClient && retailerValues.length > 0 && staticFieldsFilled;
    updateSubmitButton(allFilled && !allClientAllRetailerWarning);
  }, [step, loading, error, selectedClient, retailerValues, staticFieldsFilled, allClientAllRetailerWarning, updateSubmitButton]);

  return (
    <div className="grid grid-cols-1 gap-6">
      {/* Client Selection */}
      <div>
        <label className="required block text-sm font-medium text-gray-700">Client</label>
        {step === 1 ? (
          <Select
            isClearable
            options={clientOptions}
            value={selectedClient}
            onChange={onClientChange}
            onInputChange={(value) => setClientInputValue(value)}
            filterOption={() => true}
            placeholder="Search for a client..."
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        ) : (
          <div className="mt-1 flex items-center gap-3">
            <div className="flex-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-600">
              {selectedClient.label}
            </div>
            <button
              type="button"
              onClick={onChangeClient}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
            >
              Change
            </button>
          </div>
        )}
      </div>

      {/* Continue Button (Step 1 only) */}
      {step === 1 && (
        <div>
          <button
            type="button"
            onClick={onContinue}
            disabled={!selectedClient}
            className={`inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
              selectedClient
                ? "bg-indigo-600 hover:bg-indigo-700 cursor-pointer"
                : "bg-indigo-300 cursor-not-allowed"
            }`}
          >
            Continue
          </button>
        </div>
      )}

      {/* Loading State */}
      {step === 2 && loading && (
        <div className="flex items-center justify-center py-8">
          <svg className="animate-spin h-6 w-6 text-indigo-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          <span className="text-sm text-gray-500">Loading retailers and measures...</span>
        </div>
      )}

      {/* Error State */}
      {step === 2 && error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Dynamic Fields (Step 2, loaded) */}
      {step === 2 && !loading && !error && (
        <>
          <MultiSelectField
            label="Retailers"
            sublabel="Select one or more retailers"
            options={retailers}
            field="retailers"
            required={true}
            placeholder="Select retailers..."
            onSelectionChange={setRetailerValues}
          />

          {allClientAllRetailerWarning && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p className="text-sm text-yellow-800">
                Cannot select "all" for both Client and Retailer. Please narrow down at least one.
              </p>
            </div>
          )}

          <MultiSelectField
            label="Measures"
            sublabel="Optional — select specific measures to exclude. Cannot be combined with Measure Types or Journeys."
            options={measures}
            field="measures"
            required={false}
            isDisabled={hasMeasureTypes || hasJourneys}
            placeholder={hasMeasureTypes ? "Disabled — Measure Types selected" : hasJourneys ? "Disabled — Journeys selected" : "Select measures..."}
            onSelectionChange={setMeasureValues}
          />

          <MultiSelectField
            label="Measure Types"
            sublabel="Optional — select measure types to exclude. Cannot be combined with Measures or Journeys."
            options={measureTypes}
            field="measure_types"
            required={false}
            isDisabled={hasMeasures || hasJourneys}
            placeholder={hasMeasures ? "Disabled — Measures selected" : hasJourneys ? "Disabled — Journeys selected" : "Select measure types..."}
            onSelectionChange={setMeasureTypeValues}
          />

          {!isClientAll && (
            <MultiSelectField
              label="Journeys"
              sublabel="Optional — select journeys (search terms) to exclude. Cannot be combined with Measures or Measure Types."
              options={journeys}
              field="journeys"
              required={false}
              isDisabled={hasMeasures || hasMeasureTypes}
              placeholder={hasMeasures ? "Disabled — Measures selected" : hasMeasureTypes ? "Disabled — Measure Types selected" : "Select journeys..."}
              onSelectionChange={setJourneyValues}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ExclusionFormFields;
